import * as React from 'react';
import {useState} from 'react';
import "./topics.scss";
import {
    AddIcon,
    Alert,
    BulletsIcon,
    Button,
    CloseIcon,
    EditIcon,
    Flex,
    FlexItem,
    Header,
    List,
    Loader,
    MenuButton,
    MoreIcon,
    Text,
    Tooltip,
    TrashCanIcon
} from "@fluentui/react-northstar";
import ConfirmDialog from "../Dialogs/ConfirmDialog";
import languageObject from "../../translations";
import {AppContext, IAppContextState} from "../../services/AppContext";
import {withRouter, WithRouterProps} from "../../services/TelemetryService/withRouter.hook";

interface ITopicManagementProps extends WithRouterProps {
}

interface ITopicManagementState {
    loaded: boolean,
    deletingTopicId: string,
    showConfirmDelete: boolean,
    deleting: boolean,
}

class TopicManagement extends React.Component<ITopicManagementProps, ITopicManagementState> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    constructor(props: ITopicManagementProps) {
        super(props);

        this.state = {
            loaded: true,
            deletingTopicId: "",
            showConfirmDelete: false,
            deleting: false,
        }
    }

    render() {
        const items = new Array<object>();
        this.context.topics.list.forEach((topic) => {
            items.push({
                key: topic.id,
                disabled: this.state.deletingTopicId === topic.id,
                className: this.state.deletingTopicId === topic.id ? "deleting" : "",
                content: (
                    <Flex vAlign="center" fill gap="gap.small">
                        <Flex column fill>
                            <Text weight={"semibold"} truncated>{topic.title}</Text>
                            <Text truncated>{topic.description}</Text>
                        </Flex>
                        <Flex.Item push>
                            <MenuButton
                                className={"menuContainer"}
                                trigger={<MenuButtonTrigger onClick={e => e.stopPropagation()}/>}
                                menu={[
                                    {
                                        key: 'edit',
                                        icon: <EditIcon outline/>,
                                        content: languageObject.translate("Edit"),
                                        onClick: (event: any) => {
                                            event.stopPropagation();
                                            if (topic.id) this.onEditTopic(topic.id);
                                        }
                                    },
                                    {
                                        key: 'delete',
                                        icon: <TrashCanIcon outline/>,
                                        content: topic.unableToDelete ?
                                            <Tooltip
                                                position={"before"}
                                                align={"start"}
                                                trigger={
                                                    <Text
                                                        disabled={topic.unableToDelete}
                                                        content={languageObject.translate("Delete")}
                                                    />
                                                }
                                                content={languageObject.translate("TopicLinked")}/>
                                            :
                                            <Text content={languageObject.translate("Delete")}/>,
                                        onClick: (event: any) => {
                                            event.stopPropagation();
                                            if (topic.id !== "" && !topic.unableToDelete) {
                                                this.setState({
                                                    showConfirmDelete: true,
                                                    deletingTopicId: topic.id as string
                                                })
                                            }
                                        }
                                    },
                                ]}/>
                        </Flex.Item>
                    </Flex>
                ),
                styles: {margin: '0.3rem 0.3rem 0 0'},
                onClick: () => {
                    if (topic.id && this.state.deletingTopicId !== topic.id) this.onEditTopic(topic.id);
                },
            });
        })

        return (
            <Flex column fill gap="gap.medium" className={"p-20px"}>
                <Flex gap={"gap.small"}>
                    <Flex gap={"gap.small"} vAlign={"center"}>
                        <BulletsIcon styles={{color: "#782753"}} size={"large"}/>
                        <Header as={"h2"} styles={{margin: "0"}} content={languageObject.translate("TopicManagement")}/>
                    </Flex>
                    <FlexItem push>
                        <Button icon={<AddIcon outline/>} content={languageObject.translate("AddTopic")}
                                onClick={this.onNewTopic}
                                primary/>
                    </FlexItem>
                    <Button icon={<CloseIcon outline/>} content={languageObject.translate("CloseText")}
                            onClick={this.onClose}/>
                </Flex>
                <Flex>
                    <Alert warning content={languageObject.translate("TopicsOrgOnly")}/>
                </Flex>
                {!this.state.loaded ?
                    <Flex fill vAlign={"center"} hAlign={"center"}>
                        <Loader/>
                    </Flex>
                    :
                    items.length > 0 ?
                        <List selectable items={items} className="topics_list" truncateContent truncateHeader/>
                        :
                        <Flex fill styles={{marginTop: "10px", marginLeft: "10px"}}>
                            <Text weight={"bold"} content={languageObject.translate("EmptyTopics")}/>
                        </Flex>
                }
                <ConfirmDialog
                    show={this.state.showConfirmDelete}
                    title={languageObject.translate("RemoveTopic")}
                    subtitle={this.context.topics.list.find(t => t.id === this.state.deletingTopicId)?.title}
                    content={languageObject.translate("ConfirmRemoveTopic")}
                    onCancel={() => this.setState({showConfirmDelete: false, deletingTopicId: ""})}
                    onConfirm={() => this.onRemoveTopic()}
                    loading={this.state.deleting}
                    button={{
                        icon: <TrashCanIcon outline/>,
                        title: languageObject.translate("Remove"),
                        className: "delete_button",
                    }}
                />
            </Flex>
        );
    }

    /**
     * Create a new Topic
     */
    public onNewTopic = () => {
        this.props.navigate("/newtopic");
    }

    /**
     * Edit a topic
     * @param id topic id
     */
    public onEditTopic = (id: string) => {
        this.props.navigate("/newtopic/" + id);
    }

    public onRemoveTopic = async () => {
        this.setState({deleting: true});
        const id = this.state.deletingTopicId;
        await this.context.topics.deleteTopic(id);
        this.setState({
            deletingTopicId: "",
            deleting: false,
            showConfirmDelete: false,
        });
    }

    /**
     * On close
     */
    public onClose = () => {
        this.props.navigate("/messages");
    }
}

/**
 * Menu trigger button
 * @constructor
 */
function MenuButtonTrigger(props: {
    onClick: (e: any) => void
}) {
    const [isHovering, setHovering] = useState(true);

    return (
        <Flex styles={{padding: "7px"}}
              onMouseEnter={() => setHovering(false)}
              onMouseLeave={() => setHovering(true)}
              onClick={props.onClick}>
            <MoreIcon className={"list-more-icon"} outline={isHovering}/>
        </Flex>
    )
}

export default withRouter(TopicManagement)